import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import AsyncSelect from "react-select/async";
import { Button } from "../Button";
import { CreateKioskRequest, Kiosk } from "../../types/kiosk.type";
import { getE2GUserEmailSuggestions } from "../../services/KioskService";
import { getReactSelectStyles } from "../../utils/reactSelectStyles";

interface AddKioskModalProps {
  open: boolean;
  loading?: boolean;
  error?: string;
  mode?: "create" | "edit";
  isKioskOwner?: boolean;
  initialData?: Kiosk | null;
  onClose: () => void;
  onConfirm: (payload: CreateKioskRequest) => Promise<void> | void;
}

const AddKioskModal: React.FC<AddKioskModalProps> = ({
  open,
  loading = false,
  error,
  mode = "create",
  isKioskOwner = false,
  initialData,
  onClose,
  onConfirm,
}) => {
  const [formData, setFormData] = useState<CreateKioskRequest>({
    kioskId: "",
    name: "",
    location: "",
    region: "",
    description: "",
    tokenExpiryHours: 1,
    ownerEmail: "",
    kioskType: "PHYSICAL",
    kioskCostCents: undefined,
    monthlyLicenseFeeCents: undefined,
    commissionPercent: undefined,
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [ownerLookupError, setOwnerLookupError] = useState("");

  const loadOwnerOptions = useCallback(async (search: string) => {
    setOwnerLookupError("");
    try {
      const emails = await getE2GUserEmailSuggestions(search);
      return emails.map((email) => ({ value: email, label: email }));
    } catch (error) {
      setOwnerLookupError(error instanceof Error ? error.message : "Failed to load registered users.");
      return [];
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormData({
      kioskId: initialData?.kioskId || "",
      name: initialData?.name || "",
      location: initialData?.location || "",
      region: initialData?.region || "",
      description: initialData?.description || "",
      tokenExpiryHours: Number(initialData?.tokenExpiryHours || 1),
      ownerEmail: initialData?.ownerEmail || "",
      kioskType: initialData?.kioskType || "PHYSICAL",
      kioskCostCents:
        initialData?.kioskCostCents != null ? initialData.kioskCostCents / 100 : undefined,
      monthlyLicenseFeeCents:
        initialData?.monthlyLicenseFeeCents != null ? initialData.monthlyLicenseFeeCents / 100 : undefined,
      commissionPercent:
        initialData?.commissionPercent != null ? Number(initialData.commissionPercent) : undefined,
    });
    setValidationErrors({});
    setOwnerLookupError("");
  }, [open, initialData]);

  if (!open) {
    return null;
  }

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    const isOwnerEditMode = mode === "edit" && isKioskOwner;

    if (mode === "create" && !formData.kioskId.trim()) {
      nextErrors.kioskId = "Kiosk ID is required";
    }

    if (!formData.name.trim()) {
      nextErrors.name = "Name is required";
    }

    if (!isOwnerEditMode) {
      if (!Number.isInteger(formData.tokenExpiryHours)) {
        nextErrors.tokenExpiryHours = "Token expiry hours must be a whole number";
      } else if (formData.tokenExpiryHours < 1 || formData.tokenExpiryHours > 720) {
        nextErrors.tokenExpiryHours = "Token expiry hours must be between 1 and 720";
      }

      if (!formData.ownerEmail?.trim()) {
        nextErrors.ownerEmail = "Owner email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
        nextErrors.ownerEmail = "Owner email must be a valid email address";
      }
    }

    if (
      !isOwnerEditMode &&
      formData.commissionPercent != null &&
      (!Number.isFinite(formData.commissionPercent) ||
        formData.commissionPercent < 0 ||
        formData.commissionPercent > 100)
    ) {
      nextErrors.commissionPercent = "Commission must be between 0 and 100";
    }

    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validate()) {
      return;
    }

    const isOwnerEditMode = mode === "edit" && isKioskOwner;

    await onConfirm({
      kioskId: formData.kioskId.trim(),
      name: formData.name.trim(),
      location: formData.location?.trim() || undefined,
      region: formData.region?.trim() || undefined,
      description: isOwnerEditMode ? undefined : formData.description?.trim() || undefined,
      tokenExpiryHours: formData.tokenExpiryHours,
      ownerEmail: isOwnerEditMode ? undefined : formData.ownerEmail?.trim(),
      kioskType: isOwnerEditMode ? undefined : formData.kioskType || "PHYSICAL",
      kioskCostCents:
        isOwnerEditMode || formData.kioskCostCents == null || !Number.isFinite(formData.kioskCostCents)
          ? undefined
          : Math.round(formData.kioskCostCents * 100),
      monthlyLicenseFeeCents:
        isOwnerEditMode || formData.monthlyLicenseFeeCents == null || !Number.isFinite(formData.monthlyLicenseFeeCents)
          ? undefined
          : Math.round(formData.monthlyLicenseFeeCents * 100),
      commissionPercent:
        isOwnerEditMode || formData.commissionPercent == null || !Number.isFinite(formData.commissionPercent)
          ? undefined
          : Number(formData.commissionPercent),
    });
  };

  const isOwnerEditMode = mode === "edit" && isKioskOwner;

  const inputCls =
    "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 disabled:opacity-50";

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/50 bg-white shadow-2xl dark:border-white/10 dark:bg-slate-900"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {mode === "create" ? "Add Kiosk" : "Edit Kiosk"}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {mode === "create"
              ? "Create a new kiosk and generate its secret."
              : isOwnerEditMode
                ? "Update kiosk name, location, and region."
                : "Update kiosk details and ownership settings."}
          </p>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">

            {/* Kiosk ID */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Kiosk ID*
              </label>
              <input
                type="text"
                value={formData.kioskId}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, kioskId: event.target.value }))
                }
                disabled={mode === "edit"}
                className={inputCls}
                placeholder="e.g. kiosk-lobby-01"
              />
              {validationErrors.kioskId && (
                <p className="mt-1 text-xs text-red-500">{validationErrors.kioskId}</p>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Name*
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, name: event.target.value }))
                }
                className={inputCls}
                placeholder="e.g. Main Lobby Kiosk"
              />
              {validationErrors.name && (
                <p className="mt-1 text-xs text-red-500">{validationErrors.name}</p>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Location
              </label>
              <input
                type="text"
                value={formData.location || ""}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, location: event.target.value }))
                }
                className={inputCls}
                placeholder="e.g. Floor 2, East Entrance"
              />
            </div>

            {/* Region */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Region
              </label>
              <input
                type="text"
                value={formData.region || ""}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, region: event.target.value }))
                }
                className={inputCls}
                placeholder="e.g. NA, EMEA, APAC"
              />
            </div>

            {/* Token Expiry */}
            {!isOwnerEditMode && <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Token Expiry (Hours)*
              </label>
              <input
                type="number"
                min={1}
                max={720}
                step={1}
                value={formData.tokenExpiryHours}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    tokenExpiryHours: Number(event.target.value || 0),
                  }))
                }
                className={inputCls}
                placeholder="e.g. 2"
              />
              {validationErrors.tokenExpiryHours && (
                <p className="mt-1 text-xs text-red-500">{validationErrors.tokenExpiryHours}</p>
              )}
            </div>}

            {/* Kiosk Type */}
            {!isOwnerEditMode && <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Kiosk Type
              </label>
              <select
                value={formData.kioskType || "PHYSICAL"}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    kioskType: event.target.value as "PHYSICAL" | "DIGITAL" | "BOTH",
                  }))
                }
                className={inputCls}
              >
                <option value="PHYSICAL">Physical</option>
                <option value="DIGITAL">Digital</option>
                <option value="BOTH">Both</option>
              </select>
            </div>}

            {/* Owner Email — full width */}
            {!isOwnerEditMode && <div className="sm:col-span-2">
              <label htmlFor="kiosk-owner-email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Kiosk Owner Email <span className="text-red-500">*</span>
              </label>
              <AsyncSelect<{ value: string; label: string }, false>
                inputId="kiosk-owner-email"
                defaultOptions
                loadOptions={loadOwnerOptions}
                value={formData.ownerEmail
                  ? { value: formData.ownerEmail, label: formData.ownerEmail }
                  : null}
                onChange={(option) =>
                  setFormData((prev) => ({ ...prev, ownerEmail: option?.value || "" }))
                }
                isClearable
                isDisabled={loading}
                styles={getReactSelectStyles<{ value: string; label: string }, false>()}
                className="text-sm text-gray-900"
                placeholder="Search and select a registered user's email"
                noOptionsMessage={() => ownerLookupError ? "Unable to load registered users" : "No registered users found"}
              />
              {ownerLookupError && <p role="alert" className="mt-1 text-xs text-red-500">{ownerLookupError}</p>}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Only registered system users can be assigned as kiosk owners. Register the user first if their email is not listed.
              </p>
              {validationErrors.ownerEmail && (
                <p className="mt-1 text-xs text-red-500">{validationErrors.ownerEmail}</p>
              )}
            </div>}

            {/* Kiosk Cost */}
            {!isOwnerEditMode && <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Kiosk Cost (USD)
              </label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={formData.kioskCostCents ?? ""}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    kioskCostCents:
                      event.target.value === "" ? undefined : Number(event.target.value),
                  }))
                }
                className={inputCls}
                placeholder="e.g. 100.00"
              />
            </div>}

            {/* Monthly License Fee */}
            {!isOwnerEditMode && <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Monthly License Fee (USD)
              </label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={formData.monthlyLicenseFeeCents ?? ""}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    monthlyLicenseFeeCents:
                      event.target.value === "" ? undefined : Number(event.target.value),
                  }))
                }
                className={inputCls}
                placeholder="e.g. 15.00"
              />
            </div>}

            {/* Commission */}
            {!isOwnerEditMode && <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Commission (%)
              </label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={formData.commissionPercent ?? ""}
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    commissionPercent:
                      event.target.value === "" ? undefined : Number(event.target.value),
                  }))
                }
                className={inputCls}
                placeholder="e.g. 5"
              />
              {validationErrors.commissionPercent && (
                <p className="mt-1 text-xs text-red-500">{validationErrors.commissionPercent}</p>
              )}
            </div>}

            {/* Description — full width */}
            {!isOwnerEditMode && <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                rows={3}
                value={formData.description || ""}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, description: event.target.value }))
                }
                className={inputCls}
                placeholder="Optional notes"
              />
            </div>}
          </div>

          {error && (
            <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-white/10">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading}>
            {mode === "create" ? "Create Kiosk" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default AddKioskModal;
